/* The bell, for both apps: unread count in the top bar, the list in a panel, a toast as each one
   arrives, and a desktop notification when the tab isn't in front: hidden, or another window
   has the focus (push covers a closed app, UX-157). One server-sent event stream per tab keeps it
   current; the browser reconnects it by itself. The staff app also sends a presence heartbeat
   while a tab is in front, so notifications seen there aren't emailed later (UX-157). */

import { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Api } from "../api/client";
import { formatRelative } from "../format";
import {
  appInFront,
  desktopEnabled,
  disablePush,
  enableNotifications,
  isMobile,
  isPushActive,
  permission,
  pushCoversDevice,
  pushSupport,
  rememberPrompt,
  setDesktopEnabled,
  shouldPrompt,
  showOnce,
  syncPush,
} from "../push";
import { Button, Check, cx, errorMessage, Field } from "./basics";
import { Icon } from "./Icon";
import { useToast } from "./overlays";

/** How often the staff app tells the API it's in front (and on every focus or return). */
export const HEARTBEAT_MS = 60_000;
/** Query parameter the service worker adds when it opens the app from a notification. */
export const READ_PARAM = "notification_read";
/** "Start this meeting" (UX-158): no small toast, the staff app shows a big notice that stays
    until the meeting is opened, and its desktop notification stays until it's clicked. */
export const MEETING_START_KIND = "meeting_start";
/** Kinds that stay on screen until dealt with, rather than a toast that fades. */
export const STICKY_KINDS: ReadonlySet<string> = new Set([MEETING_START_KIND]);

export interface NotificationItem {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  link: string | null;
  created_at: string;
  read_at: string | null;
}

interface NotificationPage {
  items: NotificationItem[];
  unread: number;
  next_before: string | null;
}

interface Ctx {
  api: Api;
  items: NotificationItem[];
  unread: number;
  loaded: boolean;
  more: boolean;
  loadMore: () => Promise<void>;
  markRead: (ids?: string[]) => Promise<void>;
  /** Mark read notifications unread again (the notifications page, UX-061). */
  markUnread: (ids: string[]) => Promise<void>;
  open: (item: NotificationItem) => void;
  /** A notification arrived on the live stream since this tab opened: the moment to offer
      desktop alerts (UX-157). */
  arrived: boolean;
}

const NotificationsCtx = createContext<Ctx | null>(null);

export function useNotifications() {
  const ctx = useContext(NotificationsCtx);
  if (!ctx) throw new Error("useNotifications needs a <NotificationsProvider>");
  return ctx;
}

function readJson<T>(event: Event): T | null {
  try {
    return JSON.parse((event as MessageEvent<string>).data) as T;
  } catch {
    return null;
  }
}

/** Mark one notification read (a desktop notification was clicked). */
function readOne(
  api: Api,
  id: string,
  setItems: (f: (xs: NotificationItem[]) => NotificationItem[]) => void,
  setUnread: (n: number) => void,
) {
  const now = new Date().toISOString();
  setItems((xs) => xs.map((x) => (x.id === id && !x.read_at ? { ...x, read_at: now } : x)));
  api
    .post<{ unread: number }>("/notifications/read", { ids: [id] })
    .then((r) => setUnread(r.unread))
    .catch(() => {});
}

/** The tab's own Notification: only where there's no service worker (development, an old
    browser). Its click handler opens the item here. */
function pageNotification(title: string, options: NotificationOptions, onClick: () => void) {
  try {
    const shown = new Notification(title, options);
    shown.onclick = () => {
      onClick();
      shown.close();
    };
    return true;
  } catch {
    return false; // Android Chrome only shows them from a service worker
  }
}

/** A desktop notification from this tab, at most once per device (UX-191). With a service
    worker, push covers this device whenever the browser holds a subscription: the worker shows it,
    so the tab doesn't. Otherwise the tab shows it through the worker's registration, not as its
    own Notification: Chrome only replaces one of those with the same tag (the notification's id),
    and the worker's click handler opens it. A tag already on screen isn't shown again. */
async function showDesktop(item: NotificationItem, onClick: () => void): Promise<void> {
  const options: NotificationOptions = {
    body: item.body ?? undefined,
    tag: item.id,
    icon: "/icon.svg",
    data: { url: item.link || "/", id: item.id },
    // Stays on the desktop until it's clicked (UX-158).
    ...(STICKY_KINDS.has(item.kind) ? { requireInteraction: true } : {}),
  };
  const registration =
    "serviceWorker" in navigator
      ? await navigator.serviceWorker.getRegistration().catch(() => undefined)
      : undefined;
  if (!registration) {
    pageNotification(item.title, options, onClick);
    return;
  }
  if (await pushCoversDevice(registration)) return;
  const showing = await registration.getNotifications({ tag: item.id }).catch(() => []);
  if (showing.length > 0) return;
  try {
    await registration.showNotification(item.title, options);
  } catch {
    // A worker that isn't active yet: the tab's own, which nothing else would show.
    pageNotification(item.title, options, onClick);
  }
}

/** Wrap the signed-in app (inside the router). ``api`` is the app's client: its base URL picks
    the staff or portal notifications. ``heartbeat`` (the staff app) tells the API while a tab is
    in front, so notifications seen here aren't emailed as missed (UX-157). */
export function NotificationsProvider({
  api,
  heartbeat = false,
  children,
}: {
  api: Api;
  heartbeat?: boolean;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [before, setBefore] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [arrived, setArrived] = useState(false);

  // The presence heartbeat: on every focus or return to the tab, and every minute while it's in
  // front. Only while it's in front: an open tab in the background hasn't been looked at.
  const beatRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!heartbeat) return;
    const beat = () => {
      if (appInFront()) api.post("/notifications/presence").catch(() => {});
    };
    beatRef.current = beat;
    beat();
    const timer = setInterval(beat, HEARTBEAT_MS);
    window.addEventListener("focus", beat);
    document.addEventListener("visibilitychange", beat);
    return () => {
      beatRef.current = () => {};
      clearInterval(timer);
      window.removeEventListener("focus", beat);
      document.removeEventListener("visibilitychange", beat);
    };
  }, [api, heartbeat]);

  const open = useCallback(
    (item: NotificationItem) => {
      if (!item.read_at) {
        const now = new Date().toISOString();
        setItems((xs) => xs.map((x) => (x.id === item.id ? { ...x, read_at: now } : x)));
        setUnread((n) => Math.max(0, n - 1));
        api
          .post<{ unread: number }>("/notifications/read", { ids: [item.id] })
          .then((r) => setUnread(r.unread))
          .catch(() => {});
      }
      if (item.link) navigate(item.link);
    },
    [api, navigate],
  );
  const openRef = useRef(open);
  openRef.current = open;

  const load = useCallback(async () => {
    try {
      const page = await api.get<NotificationPage>("/notifications");
      setItems(page.items);
      setUnread(page.unread);
      setBefore(page.next_before);
      setLoaded(true);
    } catch {
      /* signed out, or offline: the stream retries */
    }
  }, [api]);

  const announce = useCallback(
    (item: NotificationItem, popup: boolean) => {
      setArrived(true);
      // A sticky kind has its own notice that stays on screen (UX-158), not a fading toast.
      if (document.visibilityState === "visible" && !STICKY_KINDS.has(item.kind))
        toast(item.title, "", () => openRef.current(item));
      if (appInFront()) {
        beatRef.current(); // seen as it arrived
        return;
      }
      // Not in front (hidden, or another window has the focus): a desktop notification, unless
      // this device turned them off or the person muted this kind.
      // One of this app's open tabs shows it, not each (UX-191).
      if (!popup || !desktopEnabled() || permission() !== "granted") return;
      void showOnce(item.id, () =>
        showDesktop(item, () => {
          window.focus();
          openRef.current(item);
        }),
      );
    },
    [toast],
  );

  useEffect(() => {
    void load();
    void syncPush(api);
    if (typeof EventSource === "undefined") return;
    const stream = new EventSource(`${api.baseUrl}/notifications/stream`, { withCredentials: true });
    // Every (re)connection starts with "ready": catch up on anything missed while away.
    stream.addEventListener("ready", () => void load());
    stream.addEventListener("resync", () => void load());
    stream.addEventListener("notification", (event) => {
      const data = readJson<NotificationItem & { unread: number; popup?: boolean }>(event);
      if (!data) return;
      const { unread: count, popup, ...item } = data;
      setItems((xs) => [item, ...xs.filter((x) => x.id !== item.id)]);
      setUnread(count);
      announce(item, popup !== false);
    });
    stream.addEventListener("read", (event) => {
      const data = readJson<{ ids: string[]; unread: number }>(event);
      if (!data) return;
      const now = new Date().toISOString();
      const ids = new Set(data.ids);
      setItems((xs) => xs.map((x) => (ids.has(x.id) && !x.read_at ? { ...x, read_at: now } : x)));
      setUnread(data.unread);
    });
    stream.addEventListener("unread", (event) => {
      const data = readJson<{ ids: string[]; unread: number }>(event);
      if (!data) return;
      const ids = new Set(data.ids);
      setItems((xs) => xs.map((x) => (ids.has(x.id) ? { ...x, read_at: null } : x)));
      setUnread(data.unread);
    });
    stream.addEventListener("signed-out", () => stream.close());
    return () => stream.close();
  }, [api, load, announce]);

  // A tap on a push notification while the app is open: the service worker asks us to go there,
  // and it's read.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string; id?: string } | null;
      if (data?.type !== "notification-click") return;
      if (data.id) readOne(api, data.id, setItems, setUnread);
      if (data.url) navigate(data.url);
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [api, navigate]);

  // Opened by the service worker from a notification (no tab was open): mark it read, and drop
  // the parameter from the address.
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get(READ_PARAM);
    if (!id) return;
    readOne(api, id, setItems, setUnread);
    params.delete(READ_PARAM);
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash },
      { replace: true },
    );
  }, [api, navigate, location]);

  const markRead = useCallback(
    async (ids?: string[]) => {
      const now = new Date().toISOString();
      const wanted = ids ? new Set(ids) : null;
      setItems((xs) =>
        xs.map((x) => (!x.read_at && (!wanted || wanted.has(x.id)) ? { ...x, read_at: now } : x)),
      );
      const r = await api.post<{ unread: number }>("/notifications/read", ids ? { ids } : {});
      setUnread(r.unread);
    },
    [api],
  );

  const markUnread = useCallback(
    async (ids: string[]) => {
      const wanted = new Set(ids);
      setItems((xs) => xs.map((x) => (wanted.has(x.id) ? { ...x, read_at: null } : x)));
      const r = await api.post<{ unread: number }>("/notifications/unread", { ids });
      setUnread(r.unread);
    },
    [api],
  );

  const loadMore = useCallback(async () => {
    if (!before) return;
    const page = await api.get<NotificationPage>("/notifications", { query: { before } });
    setItems((xs) => [...xs, ...page.items.filter((i) => !xs.some((x) => x.id === i.id))]);
    setBefore(page.next_before);
  }, [api, before]);

  return (
    <NotificationsCtx.Provider
      value={{ api, items, unread, loaded, more: !!before, loadMore, markRead, markUnread, open, arrived }}
    >
      {children}
    </NotificationsCtx.Provider>
  );
}

/* ---------- the bell and its panel ---------- */

export function NotificationBell({
  onSettings,
  onViewAll,
}: {
  onSettings?: () => void;
  /** Opens the app's notifications page, when it has one (the staff app, UX-061). */
  onViewAll?: () => void;
}) {
  const { unread } = useNotifications();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="notif" ref={wrap}>
      <button
        type="button"
        className={cx("icon-btn", "notif-btn", open && "on")}
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="bell" />
        {unread > 0 && <span className="notif-dot">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
        <NotificationPanel
          onClose={() => setOpen(false)}
          onSettings={
            onSettings &&
            (() => {
              setOpen(false);
              onSettings();
            })
          }
          onViewAll={
            onViewAll &&
            (() => {
              setOpen(false);
              onViewAll();
            })
          }
        />
      )}
    </div>
  );
}

function NotificationPanel({
  onClose,
  onSettings,
  onViewAll,
}: {
  onClose: () => void;
  onSettings?: () => void;
  onViewAll?: () => void;
}) {
  const { items, unread, loaded, more, loadMore, markRead, open } = useNotifications();
  const [busy, setBusy] = useState(false);
  return (
    <div className="notif-panel" role="dialog" aria-label="Notifications">
      <div className="notif-h">
        <h2>Notifications</h2>
        {unread > 0 && (
          <button type="button" className="link-btn" onClick={() => void markRead()}>
            Mark all read
          </button>
        )}
        <button type="button" className="icon-btn mob-only" onClick={onClose} aria-label="Close">
          <Icon name="x" />
        </button>
      </div>
      <ul className="notif-list">
        {loaded && items.length === 0 && <li className="notif-empty">You're all caught up.</li>}
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className={cx("notif-it", !item.read_at && "unread")}
              onClick={() => {
                onClose();
                open(item);
              }}
            >
              <span className="notif-t">{item.title}</span>
              {item.body && <span className="notif-b">{item.body}</span>}
              <span className="notif-w">{formatRelative(item.created_at)}</span>
            </button>
          </li>
        ))}
      </ul>
      {(more || onSettings || onViewAll) && (
        <div className="notif-f">
          {onViewAll && (
            <button type="button" className="link-btn" onClick={onViewAll}>
              View all
            </button>
          )}
          {more && !onViewAll && (
            <Button
              size="sm"
              variant="ghost"
              busy={busy}
              onClick={() => {
                setBusy(true);
                void loadMore().finally(() => setBusy(false));
              }}
            >
              Show older
            </Button>
          )}
          {onSettings && (
            <button type="button" className="link-btn" onClick={onSettings}>
              Notification settings
            </button>
          )}
        </div>
      )}
      <TurnOn />
    </div>
  );
}

/** The panel's last row until this device allows notifications: the button that asks (it must
    follow a click), or why it can't. */
function TurnOn() {
  const { api } = useNotifications();
  const toast = useToast();
  const [state, setState] = useState(() => permission());
  const [busy, setBusy] = useState(false);
  const support = pushSupport();

  if (support === "none" || state === "granted") return null;
  if (support === "ios-install")
    return (
      <p className="notif-perm muted">
        To get push notifications, add this app to your Home Screen: tap Share, then Add to Home Screen.
      </p>
    );
  if (state === "denied")
    return (
      <p className="notif-perm muted">
        Notifications are blocked. Allow them in your browser's settings for this site.
      </p>
    );
  return (
    <div className="notif-perm">
      <Button
        size="sm"
        variant="primary"
        busy={busy}
        onClick={() => {
          setBusy(true);
          void enableNotifications(api)
            .then((answer) => {
              rememberPrompt("done");
              setState(answer);
              if (answer === "granted") toast("Notifications are on for this device");
            })
            .catch((e) => toast(errorMessage(e), "err"))
            .finally(() => setBusy(false));
        }}
      >
        {isMobile() ? "Turn on push notifications" : "Turn on desktop notifications"}
      </Button>
    </div>
  );
}

/* ---------- asking to turn them on ---------- */

/** A small banner, not on first load: it appears the first time a notification arrives in this
    tab, while this device hasn't decided (UX-157). "Enable" asks the browser; "Not now" asks again
    in a fortnight. On an iPhone or iPad in Safari it says how to add the app to the Home Screen.
    ``message`` is what it offers (the portal has its own). */
export function EnableNotificationsPrompt({
  appName = "CoreCRM",
  message,
}: {
  appName?: string;
  message?: string;
}) {
  const { api, arrived } = useNotifications();
  const toast = useToast();
  const [decided, setDecided] = useState(() => !shouldPrompt());
  const [busy, setBusy] = useState(false);
  if (decided || !arrived) return null;
  const support = pushSupport();
  const phone = isMobile();
  const later = () => {
    rememberPrompt("later");
    setDecided(true);
  };

  if (support === "ios-install") {
    return (
      <div className="notif-banner" role="region" aria-label="Get notifications">
        <span>
          To get push notifications on an iPhone or iPad, add {appName} to your Home Screen: tap{" "}
          <strong>Share</strong>, then <strong>Add to Home Screen</strong>.
        </span>
        <Button
          size="sm"
          onClick={() => {
            rememberPrompt("done");
            setDecided(true);
          }}
        >
          Got it
        </Button>
      </div>
    );
  }

  async function enable() {
    setBusy(true);
    try {
      const answer = await enableNotifications(api);
      rememberPrompt("done");
      setDecided(true);
      if (answer === "granted") toast("Desktop notifications are on for this device");
      else if (answer === "denied")
        toast("Notifications are blocked. You can allow them in your browser's settings.");
    } catch (e) {
      toast(errorMessage(e), "err");
    } finally {
      setBusy(false);
    }
  }

  const wanted = message ?? "Get desktop alerts for meetings and mentions";
  const offer = phone ? wanted.replace("desktop alerts", "push notifications") : wanted;
  return (
    <div className="notif-banner" role="region" aria-label="Get notifications">
      <Icon name="bell" />
      <span>{offer}</span>
      <Button size="sm" variant="primary" busy={busy} onClick={() => void enable()}>
        Enable
      </Button>
      <Button size="sm" variant="ghost" onClick={later}>
        Not now
      </Button>
    </div>
  );
}

/* ---------- settings ---------- */

interface PrefKind {
  kind: string;
  label: string;
  /** The section it is listed under (UX-207); left out by older servers. */
  group?: string;
  emails?: boolean;
  locked?: boolean;
}

/** The kinds in the order given, split into runs that share a ``group`` (none: one run, no heading). */
export function groupKinds(kinds: PrefKind[]): { group: string | null; kinds: PrefKind[] }[] {
  const runs: { group: string | null; kinds: PrefKind[] }[] = [];
  for (const k of kinds) {
    const group = k.group || null;
    const last = runs[runs.length - 1];
    if (last && last.group === group) last.kinds.push(k);
    else runs.push({ group, kinds: [k] });
  }
  return runs;
}

interface Preferences {
  skip_seen_emails: boolean;
  muted_push_kinds: string[];
  /** Kinds whose emails are off (UX-200b); left out by older servers. */
  muted_email_kinds?: string[];
  /** ``emails``: it can be emailed (else no Email switch); ``locked``: security, always on. */
  kinds: PrefKind[];
  email_delay_minutes: number;
  missed_email_available?: boolean;
  missed_email_on?: boolean;
  missed_email_minutes?: number;
}

/** ``muted`` with ``kind`` taken out (switched on) or added (switched off). */
function toggled(muted: string[], kind: string, on: boolean): string[] {
  return on ? muted.filter((m) => m !== kind) : [...muted, kind];
}

/** Delays offered for the missed-notification email, in minutes. */
export const MISSED_EMAIL_CHOICES = [1, 2, 5, 10, 15, 30, 60];

/** The "Desktop notifications" switch for this device (UX-157): with it on, notifications pop up
    on this computer while CoreCRM isn't the tab in front, and push covers a closed app. Says why
    when the browser can't, or blocked them. */
function DesktopSwitch() {
  const { api } = useNotifications();
  const toast = useToast();
  const [state, setState] = useState(() => permission());
  const [on, setOn] = useState(() => desktopEnabled() && permission() === "granted");
  const [pushing, setPushing] = useState(() => isPushActive());
  const [busy, setBusy] = useState(false);
  const support = pushSupport();
  useEffect(() => {
    if (permission() === "granted" && desktopEnabled()) void syncPush(api).then(setPushing);
  }, [api]);

  if (support === "none")
    return (
      <p className="muted">
        This browser can't show desktop notifications. You'll still see them in the bell, and by email if you
        miss one.
      </p>
    );
  if (support === "ios-install")
    return (
      <p className="muted">
        On an iPhone or iPad, add this app to your Home Screen (Share, then Add to Home Screen) to get
        notifications.
      </p>
    );

  async function change(next: boolean) {
    setBusy(true);
    try {
      if (!next) {
        setDesktopEnabled(false);
        await disablePush(api);
        setOn(false);
        setPushing(false);
        return;
      }
      const answer = permission() === "granted" ? "granted" : await enableNotifications(api);
      rememberPrompt("done");
      setState(answer);
      if (answer !== "granted") {
        if (answer === "denied")
          toast("Notifications are blocked. Allow them in your browser's settings.", "err");
        return;
      }
      setDesktopEnabled(true);
      setOn(true);
      setPushing(await syncPush(api));
    } catch (e) {
      toast(errorMessage(e), "err");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Check checked={on} disabled={busy || state === "denied"} onChange={(next) => void change(next)}>
        Desktop notifications on this device
      </Check>
      {state === "denied" ? (
        <p className="muted">
          Notifications are blocked for this site. Allow them in your browser's settings, then turn this on.
        </p>
      ) : (
        <p className="muted">
          {on
            ? pushing
              ? "They pop up while CoreCRM isn't the tab in front, even with it closed."
              : "They pop up while CoreCRM is open but isn't the tab in front."
            : "Off: notifications only show in the bell on this device."}
        </p>
      )}
    </>
  );
}

/** Which notifications pop up on your devices and which are emailed (one switch each, UX-200b),
    whether emails you've already seen are skipped, and (staff) whether a notification you miss is
    emailed. Saves as you change it. */
export function NotificationSettings() {
  const headId = useId();
  const { api } = useNotifications();
  const toast = useToast();
  const [prefs, setPrefs] = useState<Preferences | null>(null);

  useEffect(() => {
    api
      .get<Preferences>("/notifications/preferences")
      .then(setPrefs)
      .catch((e) => toast(errorMessage(e), "err"));
  }, [api, toast]);

  async function save(next: Preferences) {
    const before = prefs;
    setPrefs(next);
    try {
      setPrefs(
        await api.put<Preferences>("/notifications/preferences", {
          skip_seen_emails: next.skip_seen_emails,
          muted_push_kinds: next.muted_push_kinds,
          ...(next.muted_email_kinds ? { muted_email_kinds: next.muted_email_kinds } : {}),
          ...(next.missed_email_available
            ? { missed_email_on: next.missed_email_on, missed_email_minutes: next.missed_email_minutes }
            : {}),
        }),
      );
    } catch (e) {
      setPrefs(before);
      toast(errorMessage(e), "err");
    }
  }

  const minutes = prefs?.missed_email_minutes ?? 5;
  return (
    <div className="stack notif-settings">
      <section className="stack">
        <h3>This device</h3>
        <DesktopSwitch />
      </section>
      {prefs && (
        <>
          <section className="stack">
            <h3>What you hear about</h3>
            <p className="muted">
              Everything still shows in the bell. Pop up turns desktop and phone notifications on or off;
              Email turns that kind's emails on or off. Security notices are always on.
            </p>
            <table className="notif-kinds">
              <thead>
                <tr>
                  <th scope="col">Notification</th>
                  <th scope="col">Pop up on my devices</th>
                  <th scope="col">Email</th>
                </tr>
              </thead>
              {groupKinds(prefs.kinds).map((run, i) => (
                <tbody key={`${run.group}-${i}`} aria-labelledby={run.group ? `${headId}-${i}` : undefined}>
                  {run.group && (
                    <tr className="notif-group">
                      <th scope="rowgroup" colSpan={3} id={`${headId}-${i}`}>
                        {run.group}
                      </th>
                    </tr>
                  )}
                  {run.kinds.map((k) => {
                    const mutedEmail = prefs.muted_email_kinds ?? [];
                    return (
                      <tr key={k.kind}>
                        <th scope="row">{k.label}</th>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Pop up: ${k.label}`}
                            checked={k.locked || !prefs.muted_push_kinds.includes(k.kind)}
                            disabled={k.locked}
                            onChange={(e) =>
                              void save({
                                ...prefs,
                                muted_push_kinds: toggled(prefs.muted_push_kinds, k.kind, e.target.checked),
                              })
                            }
                          />
                        </td>
                        <td>
                          {k.emails === false ? (
                            <span className="muted" title="Never emailed">
                              —
                            </span>
                          ) : (
                            <input
                              type="checkbox"
                              aria-label={`Email: ${k.label}`}
                              checked={k.locked || !mutedEmail.includes(k.kind)}
                              disabled={k.locked || prefs.muted_email_kinds === undefined}
                              onChange={(e) =>
                                void save({
                                  ...prefs,
                                  muted_email_kinds: toggled(mutedEmail, k.kind, e.target.checked),
                                })
                              }
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
          </section>
          <section className="stack">
            <h3>Email</h3>
            {prefs.missed_email_available && (
              <>
                <Check
                  checked={!!prefs.missed_email_on}
                  onChange={(on) => void save({ ...prefs, missed_email_on: on })}
                >
                  {`Email me if I miss a notification for ${minutes} minutes`}
                </Check>
                {prefs.missed_email_on && (
                  <div className="form">
                    <Field label="Wait">
                      <select
                        value={minutes}
                        onChange={(e) =>
                          void save({ ...prefs, missed_email_minutes: Number(e.target.value) })
                        }
                      >
                        {MISSED_EMAIL_CHOICES.map((m) => (
                          <option key={m} value={m}>
                            {m === 1 ? "1 minute" : `${m} minutes`}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                )}
                <p className="muted">
                  Missed means still unread, and CoreCRM wasn't the tab in front of you since it arrived.
                  Several at once come in one email. Never for Gmail sync notices, kinds whose Email is off,
                  or notifications that already come with their own email.
                </p>
              </>
            )}
            <Check
              checked={prefs.skip_seen_emails}
              onChange={(on) => void save({ ...prefs, skip_seen_emails: on })}
            >
              Skip the email when I've already seen the notification
            </Check>
            <p className="muted">
              {prefs.skip_seen_emails
                ? `Emails wait ${prefs.email_delay_minutes} minutes, and aren't sent if you read the notification first. Security notices and quotes are always emailed.`
                : "Every email is sent straight away."}
            </p>
          </section>
        </>
      )}
    </div>
  );
}
